import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, User, Phone, Car, FileText, Edit2, Trash2, 
  Upload, Calendar, Clock, MapPin, Truck, Search,
  CheckCircle, XCircle, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Drivers = () => {
  const { theme } = useTheme();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [todaySchedule, setTodaySchedule] = useState({ pickups: [], drops: [] });
  const [showSchedule, setShowSchedule] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    dl_number: '',
    aadhaar_number: '',
    vehicle_number: '',
    vehicle_type: 'Car'
  });

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('garage_token')}`,
    'Content-Type': 'application/json'
  });

  // Fetch drivers
  const fetchDrivers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/garage/drivers/?include_inactive=true`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setDrivers(data.drivers);
      }
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch today's schedule
  const fetchTodaySchedule = async () => {
    try {
      const res = await fetch(`${API_URL}/api/garage/drivers/schedule/today`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setTodaySchedule({ pickups: data.pickups, drops: data.drops });
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    }
  };

  useEffect(() => {
    fetchDrivers();
    fetchTodaySchedule();
  }, []);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingDriver 
        ? `${API_URL}/api/garage/drivers/${editingDriver.id}/`
        : `${API_URL}/api/garage/drivers/`;
      
      const res = await fetch(url, {
        method: editingDriver ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(editingDriver ? 'Driver updated successfully' : 'Driver added successfully');
        setShowAddDialog(false);
        setEditingDriver(null);
        resetForm();
        fetchDrivers();
      } else {
        toast.error(data.detail || 'Failed to save driver');
      }
    } catch (error) {
      console.error('Failed to save driver:', error);
      toast.error('Failed to save driver');
    }
  };

  // Delete driver
  const handleDelete = async (driverId) => {
    if (!confirm('Are you sure you want to remove this driver?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/garage/drivers/${driverId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Driver removed successfully');
        fetchDrivers();
      } else {
        toast.error(data.detail || 'Failed to remove driver');
      }
    } catch (error) {
      console.error('Failed to delete driver:', error);
      toast.error('Failed to remove driver');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      dl_number: '',
      aadhaar_number: '',
      vehicle_number: '',
      vehicle_type: 'Car'
    });
  };

  // Edit driver
  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      phone: driver.phone,
      dl_number: driver.dl_number,
      aadhaar_number: driver.aadhaar_number,
      vehicle_number: driver.vehicle_number || '',
      vehicle_type: driver.vehicle_type || 'Car'
    });
    setShowAddDialog(true);
  };

  // Filter drivers
  const filteredDrivers = drivers.filter(driver => 
    driver.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    driver.phone.includes(searchQuery) ||
    driver.dl_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeDrivers = filteredDrivers.filter(d => d.is_active);
  const inactiveDrivers = filteredDrivers.filter(d => !d.is_active);

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.pageBg} p-4 md:p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className={`text-2xl md:text-3xl font-bold ${theme.text}`}>
              Driver Management
            </h1>
            <p className={theme.textMuted}>
              Manage drivers for pickup and delivery
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSchedule(!showSchedule)}
              className={`${theme.cardBgSolid} ${theme.border}`}
              data-testid="toggle-schedule-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Today's Schedule
              {(todaySchedule.pickups.length + todaySchedule.drops.length) > 0 && (
                <Badge className="ml-2 bg-orange-500">
                  {todaySchedule.pickups.length + todaySchedule.drops.length}
                </Badge>
              )}
            </Button>
            
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) {
                setEditingDriver(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-driver-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Driver
                </Button>
              </DialogTrigger>
              <DialogContent className={`${theme.cardBgSolid} ${theme.border} max-w-lg`}>
                <DialogHeader>
                  <DialogTitle className={theme.text}>
                    {editingDriver ? 'Edit Driver' : 'Add New Driver'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className={theme.textSecondary}>Full Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter driver name"
                        required
                        className={`${theme.inputBg} ${theme.border}`}
                        data-testid="driver-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label className={theme.textSecondary}>Phone Number *</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="10 digit number"
                        required
                        maxLength={10}
                        className={`${theme.inputBg} ${theme.border}`}
                        data-testid="driver-phone-input"
                      />
                    </div>
                    
                    <div>
                      <Label className={theme.textSecondary}>Vehicle Type</Label>
                      <Select
                        value={formData.vehicle_type}
                        onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                      >
                        <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                          <SelectItem value="Car">Car</SelectItem>
                          <SelectItem value="Bike">Bike</SelectItem>
                          <SelectItem value="Tow Truck">Tow Truck</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className={theme.textSecondary}>DL Number *</Label>
                      <Input
                        value={formData.dl_number}
                        onChange={(e) => setFormData({ ...formData, dl_number: e.target.value.toUpperCase() })}
                        placeholder="Driving License"
                        required
                        className={`${theme.inputBg} ${theme.border}`}
                        data-testid="driver-dl-input"
                      />
                    </div>
                    
                    <div>
                      <Label className={theme.textSecondary}>Aadhaar Number *</Label>
                      <Input
                        value={formData.aadhaar_number}
                        onChange={(e) => setFormData({ ...formData, aadhaar_number: e.target.value })}
                        placeholder="12 digit Aadhaar"
                        required
                        maxLength={12}
                        className={`${theme.inputBg} ${theme.border}`}
                        data-testid="driver-aadhaar-input"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label className={theme.textSecondary}>Vehicle Number</Label>
                      <Input
                        value={formData.vehicle_number}
                        onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value.toUpperCase() })}
                        placeholder="Driver's vehicle number"
                        className={`${theme.inputBg} ${theme.border}`}
                        data-testid="driver-vehicle-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddDialog(false)}
                      className={`flex-1 ${theme.border}`}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      data-testid="save-driver-btn"
                    >
                      {editingDriver ? 'Update Driver' : 'Add Driver'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Today's Schedule Panel */}
        {showSchedule && (
          <Card className={`${theme.cardBgSolid} ${theme.border}`}>
            <CardHeader>
              <CardTitle className={`${theme.text} flex items-center gap-2`}>
                <Calendar className="w-5 h-5 text-orange-500" />
                Today's Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pickups */}
                <div>
                  <h3 className={`font-semibold ${theme.text} mb-3 flex items-center gap-2`}>
                    <Truck className="w-4 h-4 text-green-500" />
                    Pickups ({todaySchedule.pickups.length})
                  </h3>
                  {todaySchedule.pickups.length === 0 ? (
                    <p className={`${theme.textMuted} text-sm`}>No pickups scheduled for today</p>
                  ) : (
                    <div className="space-y-2">
                      {todaySchedule.pickups.map((pickup) => (
                        <div
                          key={pickup.id}
                          className={`p-3 rounded-lg ${theme.hoverBg} border ${theme.border}`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className={`font-medium ${theme.text}`}>{pickup.customer_name}</p>
                              <p className={`text-sm ${theme.textMuted}`}>{pickup.vehicle_number}</p>
                            </div>
                            <Badge className="bg-green-500/20 text-green-500">
                              {pickup.pickup_info?.time_slot}
                            </Badge>
                          </div>
                          <p className={`text-xs ${theme.textMuted} mt-2`}>
                            Driver: {pickup.pickup_info?.driver_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Drops */}
                <div>
                  <h3 className={`font-semibold ${theme.text} mb-3 flex items-center gap-2`}>
                    <MapPin className="w-4 h-4 text-blue-500" />
                    Deliveries ({todaySchedule.drops.length})
                  </h3>
                  {todaySchedule.drops.length === 0 ? (
                    <p className={`${theme.textMuted} text-sm`}>No deliveries scheduled for today</p>
                  ) : (
                    <div className="space-y-2">
                      {todaySchedule.drops.map((drop) => (
                        <div
                          key={drop.id}
                          className={`p-3 rounded-lg ${theme.hoverBg} border ${theme.border}`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className={`font-medium ${theme.text}`}>{drop.customer_name}</p>
                              <p className={`text-sm ${theme.textMuted}`}>{drop.vehicle_number}</p>
                            </div>
                            <Badge className="bg-blue-500/20 text-blue-500">
                              {drop.drop_info?.time_slot}
                            </Badge>
                          </div>
                          <p className={`text-xs ${theme.textMuted} mt-2`}>
                            Driver: {drop.drop_info?.driver_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, phone, or DL number..."
            className={`pl-10 ${theme.inputBg} ${theme.border}`}
            data-testid="driver-search-input"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`${theme.cardBgSolid} ${theme.border}`}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-orange-500">{activeDrivers.length}</p>
              <p className={`text-sm ${theme.textMuted}`}>Active Drivers</p>
            </CardContent>
          </Card>
          <Card className={`${theme.cardBgSolid} ${theme.border}`}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-500">{todaySchedule.pickups.length}</p>
              <p className={`text-sm ${theme.textMuted}`}>Today's Pickups</p>
            </CardContent>
          </Card>
          <Card className={`${theme.cardBgSolid} ${theme.border}`}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-500">{todaySchedule.drops.length}</p>
              <p className={`text-sm ${theme.textMuted}`}>Today's Deliveries</p>
            </CardContent>
          </Card>
          <Card className={`${theme.cardBgSolid} ${theme.border}`}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-500">
                {drivers.reduce((sum, d) => sum + (d.total_pickups || 0) + (d.total_drops || 0), 0)}
              </p>
              <p className={`text-sm ${theme.textMuted}`}>Total Trips</p>
            </CardContent>
          </Card>
        </div>

        {/* Active Drivers Grid */}
        <div>
          <h2 className={`text-lg font-semibold ${theme.text} mb-4`}>
            Active Drivers ({activeDrivers.length})
          </h2>
          
          {activeDrivers.length === 0 ? (
            <Card className={`${theme.cardBgSolid} ${theme.border}`}>
              <CardContent className="p-8 text-center">
                <User className={`w-12 h-12 mx-auto ${theme.textMuted} mb-4`} />
                <p className={theme.textMuted}>No drivers found</p>
                <p className={`text-sm ${theme.textMuted}`}>Add your first driver to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeDrivers.map((driver) => (
                <Card
                  key={driver.id}
                  className={`${theme.cardBgSolid} ${theme.border} hover:border-orange-500/50 transition-colors`}
                  data-testid={`driver-card-${driver.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-bold text-lg">
                          {driver.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className={`font-semibold ${theme.text}`}>{driver.name}</h3>
                          <p className={`text-sm ${theme.textMuted}`}>
                            <Phone className="w-3 h-3 inline mr-1" />
                            {driver.phone}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-green-500/20 text-green-500">Active</Badge>
                    </div>
                    
                    <div className={`mt-4 space-y-2 text-sm ${theme.textSecondary}`}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-orange-500" />
                        <span>DL: {driver.dl_number}</span>
                      </div>
                      {driver.vehicle_number && (
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-blue-500" />
                          <span>{driver.vehicle_type}: {driver.vehicle_number}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                        <span className="flex items-center gap-1">
                          <Truck className="w-4 h-4 text-green-500" />
                          {driver.total_pickups || 0} pickups
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-blue-500" />
                          {driver.total_drops || 0} drops
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(driver)}
                        className={`flex-1 ${theme.border}`}
                        data-testid={`edit-driver-${driver.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(driver.id)}
                        className="text-red-500 hover:text-red-400 border-red-500/30"
                        data-testid={`delete-driver-${driver.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Inactive Drivers */}
        {inactiveDrivers.length > 0 && (
          <div>
            <h2 className={`text-lg font-semibold ${theme.textMuted} mb-4`}>
              Inactive Drivers ({inactiveDrivers.length})
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inactiveDrivers.map((driver) => (
                <Card
                  key={driver.id}
                  className={`${theme.cardBgSolid} ${theme.border} opacity-60`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center text-white font-bold text-lg">
                          {driver.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className={`font-semibold ${theme.text}`}>{driver.name}</h3>
                          <p className={`text-sm ${theme.textMuted}`}>{driver.phone}</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Inactive</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Drivers;
